<template>
  <Container class="flex flex-col items-center">
    <div :class="data?.position === 'center' && 'text-center'" class="w-full max-w-[650px]">
      <SubTitle v-if="data?.subtitle" class="text-blue">
        {{ data.subtitle }}
      </SubTitle>
      <Title
        v-if="data?.title"
        :custom-style="
          twMerge('mb-5 leading-none lg:mb-6 lg:leading-none', useDarkMode ? 'text-white' : '')
        "
      >
        {{ data.title }}
      </Title>
      <div
        v-if="data?.copy"
        class="prose lg:prose-lg prose-p:text-lg prose-a:text-blue prose-a:no-underline hover:prose-a:text-blue-dark lg:prose-p:text-xl font-sans text-base leading-[21px] lg:leading-[26px]"
        :class="[
          data.position === 'center' && 'mx-auto',
          data.color === 'blue'
            ? 'font-bold !text-blue [&_p]:font-bold [&_p]:text-blue'
            : useDarkMode
              ? 'prose-p:text-white text-white'
              : 'text-black',
        ]"
        v-html="data.copy"
      />
      <div
        v-if="data?.body"
        class="prose lg:prose-lg prose-p:text-lg prose-a:text-blue prose-a:no-underline hover:prose-a:text-blue-dark lg:prose-p:text-xl custom-list pt-4 font-sans text-base leading-[21px] lg:leading-[26px]"
        :class="twMerge(blockquoteStyles, useDarkMode ? 'prose-p:text-white !text-white' : '')"
        v-html="data.body"
      />
      <component
        :is="linkComponent"
        v-if="data?.link?.url && linkComponent"
        :title="data?.link?.title"
        :link="data.link.url"
        :external="data?.link?.type === 'ext'"
        :type="linkTypeForComponent"
        v-bind="linkProps"
        class="mt-5 lg:mt-6"
        :class="buttonClass"
      />

      <slot name="actions" />
    </div>
  </Container>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import SubTitle from '../atoms/SubTitle.vue'
import Title from '../atoms/Title.vue'
import Link from '../atoms/Link.vue'
import LinkSecondary from '../atoms/LinkSecondary.vue'
import Container from '../atoms/Container.vue'
import type { ILinkData } from '../../types'
import { twMerge } from 'tailwind-merge'

const blockquoteStyles = [
  'prose-blockquote:bg-blue-hover',
  'prose-blockquote:max-w-[650px]',
  'prose-blockquote:not-italic',
  'prose-blockquote:p-4',
  'prose-blockquote:md:p-8',
  'prose-blockquote:border-none',
  'prose-blockquote:relative',
  'prose-blockquote:mx-auto',
  'prose-blockquote:w-fit',
  'prose-blockquote:rounded-2xl',
  'prose-blockquote:rounded-tl-none',
].join(' ')

export interface IIntroBlock {
  id: string
  position?: string
  subtitle?: string
  title?: string
  body?: string
  copy?: string
  link?: ILinkData
  color?: string
  linkType?: string
}

interface Props {
  data: IIntroBlock
}

const props = defineProps<Props>()

const settings = inject<string[]>('settings', [])
const useDarkMode = computed(() => settings?.includes('dark_theme') || false)

// Which component to render
const linkComponent = computed(() => {
  return props.data.linkType === 'blue-arrow' ? LinkSecondary : Link
})

// type prop for LinkSecondary
const linkTypeForComponent = computed(() => {
  return props.data?.linkType === 'blue-arrow' ? 'blue' : undefined
})

// For Link (extra props)
const linkProps = computed(() => {
  switch (props.data?.linkType) {
    case undefined:
    case 'blue-arrow':
      return {}
    case 'blue':
      return { type: 'fill', color: 'blue' }
    case 'blue-outline':
      return { type: 'outline', color: 'blue' }
    case 'pink':
      return { type: 'fill', color: 'pink' }
    case 'pink-outline':
      return { type: 'outline', color: 'pink' }
    default:
      return { type: 'fill', color: 'blue' }
  }
})

const buttonClass = computed(() => {
  return [
    props.data?.position === 'center' && props.data?.linkType === 'blue-arrow'
      ? 'flex justify-center'
      : '',
  ]
})
</script>

<style scoped>
:deep(.prose blockquote p) {
  margin-bottom: 0;
}
:deep(.prose blockquote p:first-of-type::before),
:deep(.prose blockquote p:last-of-type::after) {
  content: none !important;
}
</style>
